'use client';

import { MarketplaceProductTypeEnum } from '@ria-client/supergraph/marketplace/constants';
import { DisclosureTooltip } from '../DisclosureTooltip/DisclosureTooltip';

export interface AdvertisingDisclosureProps {
  bold?: boolean;
  textColor?: string;
  marketplaceProductType?: MarketplaceProductTypeEnum;
  className?: string;
}

function InvestingDisclosureContent() {
  return (
    <>
      <p className="text-base">
        NerdWallet, Inc. is an independent publisher and comparison service, not an investment
        advisor. Its articles, interactive tools and other content are provided to you for free, as
        self-help tools and for informational purposes only. They are not intended to provide
        investment advice. NerdWallet does not and cannot guarantee the accuracy or applicability of
        any information in regard to your individual circumstances. Examples are hypothetical, and
        we encourage you to seek personalized advice from qualified professionals regarding specific
        investment issues. Our estimates are based on past market performance, and past performance
        is not a guarantee of future performance.
      </p>
      <p className="text-base">
        We believe everyone should be able to make financial decisions with confidence. And while
        our site doesn&apos;t feature every company or financial product available on the market,
        we&apos;re proud that the guidance we offer, the information we provide and the tools we
        create are objective, independent, straightforward — and free.
      </p>
      <p className="text-base">
        So how do we make money? Our partners compensate us. This may influence which products we
        review and write about (and where those products appear on the site), but it in no way
        affects our recommendations or advice, which are grounded in thousands of hours of research.
        Our partners cannot pay us to guarantee favorable reviews of their products or services.{' '}
        <a href="https://www.nerdwallet.com/p/our-partners" className="underline">
          Here is a list of our partners.
        </a>
      </p>
    </>
  );
}

function DefaultDisclosureContent() {
  return (
    <>
      <p className="text-base">
        We believe everyone should be able to make financial decisions with confidence. While we
        don&apos;t cover every company or financial product on the market, we work hard to share a
        wide range of offers and objective editorial perspectives.
      </p>
      <p className="text-base">
        So how do we make money? Our partners compensate us for advertisements that appear on our
        site. This compensation helps us provide tools and services - like free credit score access
        and monitoring. With the exception of mortgage, home equity and other home-lending products
        or services, partner compensation is one of several factors that may affect which products
        we highlight and where they appear on our site. Other factors include your credit profile,
        product availability and proprietary website methodologies.
      </p>
      <p className="text-base">
        However, these factors{' '}
        <span className="font-medium">do not influence our editors&apos; opinions or ratings</span>,
        which are based on independent research and analysis. Our partners cannot pay us to
        guarantee favorable reviews.{' '}
        <a href="https://www.nerdwallet.com/p/our-partners" className="underline">
          Here is a list of our partners.
        </a>
      </p>
    </>
  );
}

function DisclosureContent({
  marketplaceProductType,
}: Pick<AdvertisingDisclosureProps, 'marketplaceProductType'>) {
  const Content =
    marketplaceProductType === MarketplaceProductTypeEnum.Investing
      ? InvestingDisclosureContent
      : DefaultDisclosureContent;

  return (
    <div className="mb-1 flex flex-col gap-6">
      <h3 className="font-bold text-primary">
        <span className="block">You&apos;re our first priority.</span>
        <span className="block">Every time.</span>
      </h3>
      <Content />
    </div>
  );
}

export function AdvertisingDisclosure({
  bold = false,
  textColor,
  marketplaceProductType,
  className,
}: AdvertisingDisclosureProps): React.JSX.Element {
  return (
    <DisclosureTooltip
      className={['max-w-[578px]', className].filter(Boolean).join(' ')}
      trigger={
        <button
          type="button"
          aria-label="Advertiser disclosure"
          className={[
            'typography-text-small bg-transparent p-0 hover:underline',
            bold ? 'font-medium' : 'font-light',
            textColor === undefined ? 'text-primary' : '',
          ].join(' ')}
          style={textColor === undefined ? undefined : { color: textColor }}
        >
          Advertiser disclosure
        </button>
      }
    >
      <DisclosureContent marketplaceProductType={marketplaceProductType} />
    </DisclosureTooltip>
  );
}
